import Vue from "vue";
import { doc, getDoc } from "firebase/firestore";
import { db } from "@/fb";
import { getAuthContext } from "@/authContext";

const initialState = {
  authContext: null,
  commissionData: null,
  commissionError: "",
  delegateData: null,
  delegateLoading: false,
  delegateError: "",
  loaded: false,
  contextKey: "",
};

const state = Vue.observable({ ...initialState });

const buildContextKey = (context) => {
  if (!context) {
    return "";
  }

  return `${context.cohort}|${context.commissionId}|${context.role || "delegate"}|${context.delegateId || context.documento || ""}`;
};

const normalizeText = (value) => String(value || "").trim().toLowerCase();

const findPresidentByDocument = (commissionData, documento) => {
  const presidents = Array.isArray(commissionData && commissionData.presidents)
    ? commissionData.presidents
    : [];
  const normalizedDocument = normalizeText(documento);

  return (
    presidents.find((president) => {
      const ti = president && (president.ti || president.documento || president.id);
      return normalizeText(ti) === normalizedDocument;
    }) || null
  );
};

export const resetDelegateState = () => {
  state.authContext = null;
  state.commissionData = null;
  state.commissionError = "";
  state.delegateData = null;
  state.delegateLoading = false;
  state.delegateError = "";
  state.loaded = false;
  state.contextKey = "";
};

export const loadDelegateProfile = async ({ force = false } = {}) => {
  const context = getAuthContext();
  if (!context) {
    resetDelegateState();
    return null;
  }

  const nextKey = buildContextKey(context);
  if (!force && state.loaded && state.contextKey === nextKey) {
    return state.delegateData;
  }

  state.authContext = context;
  state.contextKey = nextKey;
  state.delegateLoading = true;
  state.commissionError = "";
  state.delegateError = "";

  try {
    const commissionRef = doc(
      db,
      "commissions",
      context.cohort,
      "commissions",
      context.commissionId
    );

    const shouldLoadDelegate = String(context.role || "delegate").toLowerCase() !== "president";
    const delegateRef = shouldLoadDelegate
      ? doc(
          db,
          "commissions",
          context.cohort,
          "commissions",
          context.commissionId,
          "delegates",
          context.delegateId
        )
      : null;

    const [commissionSnapshot, delegateSnapshot] = await Promise.all([
      getDoc(commissionRef),
      shouldLoadDelegate ? getDoc(delegateRef) : Promise.resolve(null),
    ]);

    state.commissionData = commissionSnapshot.exists()
      ? commissionSnapshot.data()
      : null;

    if (String(context.role || "delegate").toLowerCase() === "president") {
      const presidentData = findPresidentByDocument(state.commissionData, context.documento);

      if (!state.commissionData || !presidentData) {
        throw new Error("President not found in commission document");
      }

      state.delegateData = {
        ...presidentData,
        role: "president",
        documento: context.documento,
        delegation: presidentData.delegation || { id: context.documento },
      };
    } else {
      state.delegateData = delegateSnapshot && delegateSnapshot.exists()
        ? delegateSnapshot.data()
        : null;
    }

    state.loaded = true;
    return {
      commissionData: state.commissionData,
      delegateData: state.delegateData,
    };
  } catch (error) {
    state.commissionError =
      "Error consultando Firestore. Verifica reglas, ruta y credenciales.";
    state.delegateError =
      String(context.role || "delegate").toLowerCase() === "president"
        ? "No se encontró el presidente en la comisión indicada."
        : "Error consultando Firestore. Verifica reglas, ruta y credenciales.";
    state.loaded = true;
    return null;
  } finally {
    state.delegateLoading = false;
  }
};

const delegateStore = {
  state,
  loadDelegateProfile,
  resetDelegateState,
};

export default delegateStore;
